// Actions on one or more registrations, shared by the registrations page and
// the entry editor. Each one reports its own result with a toast.

import '../messages/entries';
import '../messages/ui';
import { data } from '../stores/data.svelte';
import { timer } from '../stores/timer.svelte';
import { t } from '../i18n.svelte';
import { toast, confirm } from '../toast.svelte';
import { errorMessage } from '../api';
import { totals } from '../analytics';
import { formatDuration, formatMoney } from '../format';
import { restoreEntries } from './helpers';
import type { TimeEntry } from '../types';

/** Runs a write and toasts its success or error. */
async function run(fn: () => Promise<unknown>, message: string): Promise<void> {
  try {
    await fn();
    toast.success(message);
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

export function markEntries(list: TimeEntry[], status: 'open' | 'paid' | 'non_billable'): Promise<void> {
  const ids = list.map((e) => e.id);
  if (!ids.length) return Promise.resolve();
  return run(() => data.recordPayment(ids, status), t('entries.marked', { count: ids.length, status: t(`payment.${status}`).toLocaleLowerCase() }));
}

export function toggleBillable(list: TimeEntry[]): Promise<void> {
  const ids = list.map((e) => e.id);
  return run(() => data.bulkToggleBillable(ids), t('entries.billable_toggled', { count: ids.length }));
}

// ---------- Delete with undo ----------

export function confirmDeleteOne(): Promise<boolean> {
  return confirm({ title: t('entries.delete_one_title'), message: t('entries.delete_one_message'), confirmLabel: t('common.delete'), danger: true, skippable: true });
}

export function confirmDeleteMany(list: TimeEntry[]): Promise<boolean> {
  const sum = totals(list);
  return confirm({
    title: t('entries.delete_many_title', { count: list.length }),
    message: t('entries.delete_many_message', { hours: formatDuration(sum.minutes), amount: formatMoney(sum.revenue) }),
    confirmLabel: t('common.delete'),
    danger: true,
    skippable: list.length <= 5
  });
}

function undo(list: TimeEntry[]) {
  restoreEntries(list)
    .then(() => toast.success(t('entries.restored')))
    .catch((err) => toast.error(errorMessage(err)));
}

/** Runs the delete and offers undo (up to 50 entries). Returns false on an error. */
async function deleteWithUndo(list: TimeEntry[], remove: () => Promise<unknown>, message: string): Promise<boolean> {
  try {
    await remove();
  } catch (err) {
    toast.error(errorMessage(err));
    return false;
  }
  if (list.length <= 50) toast.show({ kind: 'success', message, action: { label: t('common.undo'), run: () => undo(list) } });
  else toast.success(message);
  return true;
}

export function deleteOne(e: TimeEntry): Promise<boolean> {
  return deleteWithUndo([e], () => data.deleteEntry(e.id), t('entries.deleted_one'));
}

export function deleteMany(list: TimeEntry[]): Promise<boolean> {
  return deleteWithUndo(list, () => data.deleteEntries(list.map((e) => e.id)), t('entries.deleted_many', { count: list.length }));
}

// ---------- Timer ----------

/** Starts the timer with the entry's client, activity and description. */
export async function continueInTimer(e: TimeEntry): Promise<boolean> {
  if (timer.active) {
    const ok = await confirm({ title: t('entries.replace_timer_title'), message: t('entries.replace_timer_message'), confirmLabel: t('entries.continue_timer') });
    if (!ok) return false;
  }
  try {
    await timer.start({ clientId: e.client_id, clientName: e.client_name || '', activityId: e.activity_id, activityName: e.activity_name || '', description: e.description || '', rate: e.rate });
    return true;
  } catch (err) {
    toast.error(errorMessage(err));
    return false;
  }
}
